/**
 * SOLID Review
 * S — Strong. One concern: subscribe to the player's pushed credit balance and map it.
 * O — Strong. A second payment operation is one more method and one more document.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Implements a one-method port; no consumer sees more than it needs.
 * D — Strong. Takes IPortGraphQLDomainClient — the narrow executor — and the logger, never the Apollo singleton or the
 *     console; the subscription goes through `GraphQLCall` (§28.288).
 */
/**
 * DDD Layer: Infrastructure — the Payment half of the client↔server seam.
 *
 * The executor arrives from THIS domain's socket on ComponentFrontendInfrastructure
 * (`graphQLPaymentDomainPort`), not as an imported client and not from the round socket, which is
 * where this operation used to travel. See IAdapterApolloPayment for what that cost.
 */
import { gql } from '@apollo/client';
import type { IAdapterApolloPayment, IEntityConsumedCredit } from '../../application/payment/ports/IAdapterApolloPayment';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';
import { GraphQLCall } from './GraphQLCall';

const CREDITS_UPDATED_SUB = gql`
  subscription ApolloPaymentAdapter_CreditsUpdated($playerId: ID!) {
    creditsUpdated(playerId: $playerId) { playerId newCredits }
  }
`;

export class ApolloPaymentAdapter implements IAdapterApolloPayment {
  private readonly call: GraphQLCall;

  constructor(client: IPortGraphQLDomainClient, logger: IServiceLogger) {
    this.call = new GraphQLCall('ApolloPaymentAdapter', client, logger);
  }

  subscribeToCreditsUpdated(playerId: string, onEvent: (ev: IEntityConsumedCredit) => void): () => void {
    return this.call.subscribe('creditsUpdated', (apollo) => apollo.subscribe<{ creditsUpdated: IEntityConsumedCredit }>({ query: CREDITS_UPDATED_SUB, variables: { playerId } }),
      (data) => data.creditsUpdated, onEvent);
  }
}
